import React, { useEffect } from "react";
import {
	Form, Input, Select,
	DatePicker, Card, 
	Button
} from "antd";
import { connect } from "react-redux";
import { requiredField } from "utils/crudSagaHelper";
import TextArea from "antd/es/input/TextArea";
import { getVendorList } from "../../../../redux/vendor/vendorState";
import {
	getPurchaseOrderDetails,
	doPurchaseOrderCreate,
	doPurchaseOrderUpdate,
} from "../../../../redux/purchaseorder/purchaseOrderState";
import dayjs from "dayjs";
import PurchaseOrderItemForm from "./PurchaseOrderItemForm";

const PurchaseOrderForm = ({
	doGetVendors,
	doCreate,
	doUpdate,
	getRecord,
	recordData,
	recordId,
	vendorList,
	mode = "edit",
	currentUserId,
}) => {
	const [form] = Form.useForm();

	const items = Form.useWatch("items", form) || [];
	const currency =
		Form.useWatch("por_currency", form) || "PHP";
	const isOwner = recordData?.created_by === currentUserId;
	const isApproved = recordData?.approval_status_name === "Approved";
	const isNewRecord = !recordId;

	const handleVendorSearch = (value) => {
		if (!value || value.length < 2) return;

		doGetVendors({
			keyword: value,
			paginate: 20,
		});
	};

	const vendorOptions = React.useMemo(() => {
		const list = Array.isArray(vendorList) ? vendorList : [];

		const base = list.map(v => ({
			value: v.vdr_id,
			label: v.vdr_name,
		}));

		if (recordData?.vendor) {
			const exists = base.some(v => v.value === recordData.vendor.vdr_id);

			if (!exists) {
				base.unshift({
					value: recordData.vendor.vdr_id,
					label: recordData.vendor.vdr_name,
				});
			}
		}

		return base;
	}, [vendorList, recordData]);

	useEffect(() => {
		if (recordId) {
			getRecord(recordId);
		}
	}, [recordId]);
	
	useEffect(() => {
		if (!recordId || !recordData) return;

		const items = Array.isArray(recordData.items)
			? recordData.items
			: [];

		form.setFieldsValue({
			por_id: recordData.por_id,
			por_purchase_date: recordData.por_purchase_date
				? dayjs(recordData.por_purchase_date)
				: null,
			por_acquired_date: recordData.por_acquired_date
				? dayjs(recordData.por_acquired_date)
				: null,
			por_vendor_id: recordData.por_vendor_id,
			por_currency: recordData.por_currency || "PHP",
			por_remarks: recordData.por_remarks || "",

			items: items.length
				? items.map((item) => ({
						poi_id: item.poi_id,
						poi_item_name: item.poi_item_name || "",
						poi_description: item.poi_description || "",
						poi_quantity: item.poi_quantity ?? 1,
						poi_cost: item.poi_cost ?? 0,
				}))
				: [{ poi_quantity: 1, poi_cost: 0 }],
		});
	}, [recordId, recordData, form]);

	const currencySymbols = {
		PHP: "₱",
		USD: "$",
		EUR: "€",
	};

	const getSymbol = (cur) => currencySymbols[cur] || "₱";

	const formatAmount = (amount) =>
		Number(amount || 0).toLocaleString(undefined, {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	
	const computedItems = items.map((item) => {
		const quantity = Number(item.poi_quantity ?? 1);
		const cost = Number(item.poi_cost ?? 0);

		return {
			...item,
			total: quantity * cost,
		};
	});

	const subtotal = computedItems.reduce(
		(sum, item) => sum + item.total,
		0
	);
	const vat = subtotal * 0.12;
	const shippingFee = 0;
	const total = subtotal + shippingFee + vat;

	const onSubmit = (formData) => {
		const payload = {
			por_id: formData.por_id || undefined,
			por_vendor_id: formData.por_vendor_id,
			por_purchase_date: formData.por_purchase_date,
			por_acquired_date: formData.por_acquired_date,
			por_currency: formData.por_currency,
			por_remarks: formData.por_remarks,
			items: formData.items,
			por_total_amount: total,
			action: formData.action,
		};

		if (!payload.por_id) delete payload.por_id;

		formData?.por_id ? doUpdate(payload) : doCreate(payload);



	};

	return (
		<Form
			form={form}
			layout="vertical"
			className={mode !== "view" ? "form" : ""}
			onFinish={onSubmit}
		>
			<div className="grid grid-cols-3 gap-6 h-screen">

				<div className="col-span-2 h-screen overflow-y-auto pr-2">

					<Form.Item name="por_id" hidden>
						<Input type="hidden" />
					</Form.Item>

					<Form.Item name="action" hidden>
						<Input type="hidden" />
					</Form.Item>

					<Card className="mb-4">
						<div className="grid grid-cols-3 gap-4">

							<Form.Item
								name="por_purchase_date"
								label="Purchased Date"
							>
								{mode !== "view" ? (
									<DatePicker className="input-field w-full" />
								) : (
									<label className="text-base label-value dark:text-white">
										{recordData?.por_purchase_date || "-"}
									</label>
								)}
							</Form.Item>

							<Form.Item
								name="por_acquired_date"
								label="Acquired Date"
							>
								{mode !== "view" ? (
									<DatePicker className="input-field w-full" />
								) : (
									<label className="text-base label-value dark:text-white">
										{recordData?.por_acquired_date || "-"}
									</label>
								)}
							</Form.Item>

							<Form.Item
								name="por_currency"
								label="Currency"
								initialValue="PHP"
							>
								{mode !== "view" ? (
									<Select
										className="input-field"
										options={[
											{ value: "PHP", label: "₱ PHP" },
											{ value: "USD", label: "$ USD" },
											{ value: "EUR", label: "€ EUR" },
										]}
									/>
								) : (
									<label className="text-base label-value dark:text-white">
										{recordData?.por_currency || "-"}
									</label>
								)}
							</Form.Item>

						</div>
					</Card>

					<Form.List name="items">
						{(fields, { add, remove }) => (
							<>
								{fields.map((field) => (
									<PurchaseOrderItemForm
										key={field.key}
										field={field}
										form={form}
										currency={currency}
										getSymbol={getSymbol}
										remove={remove}
										mode={mode}
									/>
								))}

								{mode !== "view" && (
									<Button
										type="dashed"
										block
										onClick={() =>
											add({
												poi_quantity: 1,
												poi_cost: 0,
											})
										}
									>
										Add Item
									</Button>
								)}
							</>
						)}
					</Form.List>

					<Card className="mt-4">
						<Form.Item name="por_remarks" label="Remarks">
							{mode !== "view" ? (
								<TextArea
									rows={4}
									className="input-field"
								/>
							) : (
								<label className="text-base label-value dark:text-white">
									{recordData?.por_remarks || "-"}
								</label>
							)}
						</Form.Item>
					</Card>
				</div>

				<div className="col-span-1 h-screen">
					<div className="sticky top-0">
						<Card>
							<div className="space-y-6">
								<div className="flex items-start justify-between gap-4">
									<div className="flex-1">
										<Form.Item
											name="por_vendor_id"
											label="Vendor"
											rules={[requiredField()]}
										>
											{mode !== "view" ? (
													<Select
														showSearch
														filterOption={false}
														onSearch={handleVendorSearch}
														options={vendorOptions}
													/>
												) : (
													<label className="text-base label-value dark:text-white">
														{recordData?.vendor?.vdr_name || "-"}
													</label>
												)}
										</Form.Item>
									</div>
								</div>

								<div>
									<h2 className="font-semibold title-text text-xl mb-4">
										Order Summary
									</h2>

									<div className="space-y-4">
										<div className="flex items-center justify-between muted-text">
											<span>Subtotal ({computedItems.length} Items)</span>
											<span>
												{getSymbol(currency)}
												{formatAmount(subtotal)}
											</span>
										</div>

										<div className="flex items-center justify-between muted-text">
											<span>Shipping Fee</span>
											<span>
												{getSymbol(currency)}
												{formatAmount(shippingFee)}
											</span>
										</div>

										<div className="flex items-center justify-between muted-text">
											<span>VAT (12%)</span>
											<span>
												{getSymbol(currency)}
												{formatAmount(vat)}
											</span>
										</div>

										<div className="border-t pt-4 flex items-center justify-between">
											<span className="text-2xl font-bold">
												Total
											</span>
											<span className="text-2xl font-bold">
												{getSymbol(currency)}
												{formatAmount(total)}
											</span>
										</div>
									</div>
									{mode === "view" && (
										<div className="border-t mt-4 pt-4">
											<div className="flex items-center justify-between">
												<span className="font-medium">
													Status
												</span>

												<span className="text-base label-value">
													{recordData?.approval_status_name || "-"}
												</span>
											</div>
										</div>
									)}
									{recordData?.approval_status_name === "New" &&
										recordData?.created_by !== currentUserId && (
											<Button
												block
												className="mt-3"
												onClick={() =>
													doUpdate({
														...recordData,
														action: "approve",
													})
												}
											>
												Approve
											</Button>
									)}
									{mode === "view" && isApproved && (
										<>
											<div className="border-t mt-4 pt-4">
												<div className="flex items-center justify-between">
													<span className="font-medium">
														Delivery Status
													</span>

													<span className="text-base label-value">
														{recordData?.delivery_status_name}
													</span>
												</div>

												{isOwner &&
													recordData?.delivery_status_name === "Pending" && (
														<Button
															block
															className="mt-3"
															onClick={() =>
																doUpdate({
																	por_id: recordData.por_id,
																	action: "for_delivery",
																})
															}
														>
															Tag as For Delivery
														</Button>
												)}

												{isOwner &&
													recordData?.delivery_status_name === "For Delivery" && (
														<Button
															block
															className="mt-3"
															onClick={() =>
																doUpdate({
																	por_id: recordData.por_id,
																	action: "received",
																})
															}
														>
															Tag as Received
														</Button>
												)}
											</div>

											<div className="border-t mt-4 pt-4">
												<div className="flex items-center justify-between">
													<span className="font-medium">
														Payment Status
													</span>

													<span className="text-base label-value">
														{recordData?.payment_status_name}
													</span>
												</div>

												{isOwner &&
													recordData?.payment_status_name === "Pending" && (
														<Button
															block
															className="mt-3"
															onClick={() =>
																doUpdate({
																	por_id: recordData.por_id,
																	action: "for_invoice",
																})
															}
														>
															Tag as For Invoice
														</Button>
												)}

												{isOwner &&
													recordData?.payment_status_name === "For Invoice" && (
														<Button
															block
															className="mt-3"
															onClick={() =>
																doUpdate({
																	por_id: recordData.por_id,
																	action: "invoice_sent",
																})
															}
														>
															Tag as Invoice Sent
														</Button>
												)}

												{isOwner &&
													recordData?.payment_status_name === "Invoice Sent" && (
														<Button
															block
															className="mt-3"
															onClick={() =>
																doUpdate({
																	por_id: recordData.por_id,
																	action: "invoice_paid",
																})
															}
														>
															Tag as Invoice Paid
														</Button>
												)}
											</div>
										</>
									)}
								</div>
							</div>
							{mode !== "view" && (
								<div className="space-y-2 mt-2">
									<Button
										block
										onClick={() => {
											form.setFieldValue("action", "draft");
											form.submit();
										}}
									>
										{isNewRecord ? "Save Draft" : "Update Draft"}
									</Button>

									<Button
										block
										onClick={() => {
											form.setFieldValue("action", "submit");
											form.submit();
										}}
									>
										Submit For Approval
									</Button>
								</div>
							)}
						</Card>
					</div>
				</div>
			</div>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	vendorList: state.vendor.vendorList?.items,
	recordData: state.purchaseOrder.purchaseOrderDetails?.data,
	currentUserId: state.auth.user?.id,
});

const mapDispatchToProps = (dispatch) => ({
	getRecord: (id) => dispatch(getPurchaseOrderDetails(id)),
	doCreate: (data) => dispatch(doPurchaseOrderCreate(data)),
	doUpdate: (data) => dispatch(doPurchaseOrderUpdate(data)),
	doGetVendors: (filters) => dispatch(getVendorList(filters)),
});
export default connect( mapStateToProps, mapDispatchToProps )(PurchaseOrderForm);
